import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { CompanyOffice } from 'src/app/interfaces/models/company-office';
import { VendorRanking } from 'src/app/interfaces/models/vendor-ranking';

import { Address } from './address';
import { BaseModel } from './base-model';
import { ChangeHistory } from './change-history';
import { Company } from './company';
import { CompanyVendor } from './company-vendor';
import { Contact } from './contact';
import { Site } from './site';
import { VendorReview } from './vendor-review';

export class ExemptVendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ExemptVendorProperties: (
    | {
        property: keyof ExemptVendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ExemptVendor;
        type: 'alias';
      }
    | {
        property: keyof ExemptVendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorTypeId', type: 'column', dataType: 'number' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'bsn', type: 'column', dataType: 'string' },
    { property: 'rsn', type: 'column', dataType: 'string' },
    { property: 'website', type: 'column', dataType: 'string' },
    { property: 'companyType', type: 'column', dataType: 'string' },
    { property: 'jurisdiction', type: 'column', dataType: 'string' },
    { property: 'llcType', type: 'column', dataType: 'string' },
    { property: 'address1', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'mailingAddress1', type: 'column', dataType: 'string' },
    { property: 'mailingAddress2', type: 'column', dataType: 'string' },
    { property: 'mailingCity', type: 'column', dataType: 'string' },
    { property: 'mailingState', type: 'column', dataType: 'string' },
    { property: 'mailingZip', type: 'column', dataType: 'string' },
    { property: 'isOnSite', type: 'column', dataType: 'boolean' },
    { property: 'isProfessional', type: 'column', dataType: 'boolean' },
    { property: 'isSupplier', type: 'column', dataType: 'boolean' },
    { property: 'is1099Issued', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },

    { property: 'CompanyVendors', model: 'CompanyVendor', type: 'association' },
    { property: 'Companies', model: 'Company', type: 'association' },
    {
      property: 'ChangeHistories',
      model: 'ChangeHistory',
      type: 'association'
    },
    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    {
      property: 'MailingAddress',
      model: 'MailingAddress',
      type: 'association'
    },
    {
      property: 'RemitToAddress',
      model: 'RemitToAddress',
      type: 'association'
    },
    { property: 'CompanyOffices', model: 'CompanyOffice', type: 'association' },
    { property: 'Contacts', model: 'Contact', type: 'association' },
    { property: 'VendorTrades', model: 'VendorTrade', type: 'association' },
    {
      property: 'VendorTradeStates',
      model: 'VendorTradeState',
      type: 'association'
    },
    { property: 'VendorRankings', model: 'VendorRanking', type: 'association' },
    { property: 'VendorReviews', model: 'VendorReview', type: 'association' },
    { property: 'Sites', model: 'Site', type: 'association' }
  ];

  static {
    ExemptVendor.ExemptVendorProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        ExemptVendor.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        ExemptVendor.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        ExemptVendor.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ExemptVendorObject: Partial<ExemptVendor>) {
    super();

    ExemptVendor.ExemptVendorProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ExemptVendorObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  ExemptVendorTypeId?: number;
  legalName?: string;
  dba?: string | null;
  ein?: string | null;
  ssn?: string | null;
  bsn?: string | null;
  rsn?: string | null;
  website?: string | null;
  companyType?:
    | 'C Corporation'
    | 'S Corporation'
    | 'Limited Liability Company (LLC)'
    | 'Partnership'
    | 'Sole Proprietorship'
    | 'Corporation'
    | null;
  jurisdiction?:
    | 'AB'
    | 'BC'
    | 'MB'
    | 'NB'
    | 'NL'
    | 'NT'
    | 'NS'
    | 'NU'
    | 'ON'
    | 'PE'
    | 'QC'
    | 'SK'
    | 'YT'
    | 'Federal'
    | 'Both'
    | null;
  llcType?: 'C' | 'S' | 'P' | 'disregardedEntity' | null;
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: string;
  zip?: string;
  country?: 'US' | 'CA';
  phone?: string | null;
  phone2?: string | null;
  fax?: string | null;
  mailingAddress1?: string;
  mailingAddress2?: string | null;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  isOnSite?: boolean;
  isProfessional?: boolean;
  isSupplier?: boolean;
  is1099Issued?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isArchived?: boolean;

  CompanyVendors?: CompanyVendor[];
  Companies?: Company[];
  ChangeHistories?: ChangeHistory[];
  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;
  RemitToAddress?: Address;
  CompanyOffices?: CompanyOffice[];
  Contacts?: Contact[];
  VendorTrades?: VendorTrade[];
  VendorTradeStates?: VendorTradeState[];
  VendorRankings?: VendorRanking[];
  VendorReviews?: VendorReview[];
  Sites?: Site[];
}
